/**
 * Shared email formatting utilities used by both frontend and backend
 * to ensure consistent behavior in email generation and preview.
 */

const calculateSpeakerDetails = (session, event, format) => {
    // Find this session's segment in the format
    const currentSegmentIndex = format?.segments?.findIndex(
        segment => segment.segment_type === session.session_type && 
                 segment.order_index === (session.speaker_order - 1)
    );

    // Get the current segment for duration and name
    const currentSegment = format?.segments?.[currentSegmentIndex];

    // Calculate start time based on previous segments
    const eventStartTime = new Date(event.event_date);
    let totalMinutes = 0;
    
    if (currentSegmentIndex !== -1 && format?.segments) {
        // Sum duration of all previous segments
        for (let i = 0; i < currentSegmentIndex; i++) {
            totalMinutes += format.segments[i].duration_minutes || 0;
        }
        eventStartTime.setMinutes(eventStartTime.getMinutes() + totalMinutes);
    }

    return {
        segmentName: currentSegment?.name || 'TBA',
        startTime: eventStartTime,
        duration: currentSegment?.duration_minutes || format?.default_duration_minutes || 0,
        segment: currentSegment
    };
};

const formatEventDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-AU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'Australia/Brisbane'
        }).format(date);
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid Date';
    }
};

const formatTime = (date) => {
    return date.toLocaleTimeString('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Australia/Brisbane'
    });
};

const generateAgendaTable = (event, format) => {
    if (!event || !format?.segments) return '';

    const eventDate = new Date(event.event_date);
    let currentTime = new Date(eventDate);
    
    // Generate table header
    let table = '<table style="width:100%; border-collapse: collapse; margin: 10px 0;">';
    table += '<tr style="background-color: #f8f9fa;">';
    table += '<th style="padding: 8px; border: 1px solid #dee2e6; text-align: left;">Time</th>';
    table += '<th style="padding: 8px; border: 1px solid #dee2e6; text-align: left;">Session</th>';
    table += '<th style="padding: 8px; border: 1px solid #dee2e6; text-align: left;">Details</th>';
    table += '</tr>';

    // Add rows for each segment
    format.segments.forEach((segment, index) => {
        // Find matching session data - note that speaker_order is 1-based
        const sessionData = event.sessions?.find(s => {
            return s.session_type === segment.segment_type && s.speaker_order === index + 1;
        });

        let details = '';
        
        // Handle different segment types
        switch (segment.segment_type) {
            case 'member_speaker':
            case 'external_speaker':
                // For speakers, show speaker name, topic, and description
                if (sessionData) {
                    let speakerDetails = [];
                    
                    if (sessionData.speaker_name) {
                        speakerDetails.push(`<strong>${sessionData.speaker_name}</strong>`);
                    }
                    
                    if (sessionData.topic && sessionData.topic !== 'Speaker 1 Topic' && sessionData.topic !== 'Speaker 2 Topic') {
                        speakerDetails.push(sessionData.topic);
                    }
                    
                    if (sessionData.description && sessionData.description !== 'Speaker 1 Description' && sessionData.description !== 'Speaker 2 Description') {
                        speakerDetails.push(`<em>${sessionData.description}</em>`);
                    }
                    
                    // If we have placeholder text, show it for now
                    if (sessionData.topic && (sessionData.topic === 'Speaker 1 Topic' || sessionData.topic === 'Speaker 2 Topic')) {
                        speakerDetails.push(sessionData.topic);
                        if (sessionData.description) {
                            speakerDetails.push(`<em>${sessionData.description}</em>`);
                        }
                    }
                    
                    details = speakerDetails.join('<br>');
                }
                break;
                
            case 'discussion':
                // For discussion items, show the topic
                details = sessionData?.topic || '';
                break;
                
            default:
                // For all other types (like arrival, welcome, etc), show topic if available
                details = sessionData?.topic || '';
        }

        // Don't show 'null', 'undefined', or '0' in the details column
        if (!details || details === 'null' || details === 'undefined' || details === '0') {
            details = '';
        }
        
        table += '<tr>';
        table += `<td style="padding: 8px; border: 1px solid #dee2e6;">${formatTime(currentTime)}</td>`;
        table += `<td style="padding: 8px; border: 1px solid #dee2e6;">${segment.name || ''}</td>`;
        table += `<td style="padding: 8px; border: 1px solid #dee2e6;">${details}</td>`;
        table += '</tr>';

        // Update time for next segment
        currentTime = new Date(currentTime.getTime() + (segment.duration_minutes || 0) * 60000);
    });

    table += '</table>';
    return table;
};

module.exports = {
    calculateSpeakerDetails,
    formatEventDate,
    formatTime,
    generateAgendaTable
}; 